import { useState } from 'react'
import { Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useInvoices } from '@/hooks/use-billing'
import { InvoiceDetailModal } from './invoice-detail-modal'
import { AmountCell, formatDate, InvoiceTypeBadge, StatusBadge } from './invoice-display-shared'
import type { Invoice } from '@riverpark/types'

/** Invoices generated for one utility type + billing period — scoped, so no filters or
 * pagination needed (bounded by plot count, not the whole ledger). */
export function CycleInvoicesPanel({
  utilityTypeId,
  billingPeriod,
}: {
  utilityTypeId: string
  billingPeriod: string
}) {
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null)
  const { data, isLoading } = useInvoices({
    utilityTypeId,
    billingPeriod,
    excludeInvoiceType: 'credit_note',
    limit: 100,
  })
  const invoices = data?.data ?? []

  if (isLoading) {
    return <p className="font-sans text-sm text-muted-foreground">Loading invoices…</p>
  }

  if (invoices.length === 0) {
    return (
      <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
        No invoices generated for this period yet.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {/* Mobile: cards, action up front */}
      <div className="space-y-2 sm:hidden">
        {invoices.map((inv) => (
          <div key={inv.id} className="rounded border border-border-2 bg-card p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="font-sans text-sm font-medium text-ink">{inv.invoiceNumber}</span>
                <StatusBadge status={inv.status} />
                <InvoiceTypeBadge invoiceType={inv.invoiceType} />
              </div>
              <Button variant="secondary" size="sm" onClick={() => setActiveInvoice(inv)}>
                <Eye size={14} className="mr-1.5" />
                View
              </Button>
            </div>
            <div className="mt-2 font-sans text-xs text-muted-foreground">
              <p>
                {inv.memberName} · Plot {inv.plotNumber}
              </p>
              <p>
                <AmountCell invoice={inv} /> · Due {formatDate(inv.dueDate)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop / tablet */}
      <div className="hidden overflow-x-auto rounded border border-border-2 sm:block">
        <table className="w-full font-sans text-sm">
          <thead>
            <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
              <th className="px-4 py-3">Invoice #</th>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Plot</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-border-2 last:border-0">
                <td className="px-4 py-3 font-medium text-ink">{inv.invoiceNumber}</td>
                <td className="px-4 py-3 text-ink">{inv.memberName}</td>
                <td className="px-4 py-3 text-ink">{inv.plotNumber}</td>
                <td className="px-4 py-3 text-ink">
                  <AmountCell invoice={inv} />
                </td>
                <td className="px-4 py-3 text-ink">{formatDate(inv.dueDate)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={inv.status} />
                    <InvoiceTypeBadge invoiceType={inv.invoiceType} />
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="secondary" size="sm" onClick={() => setActiveInvoice(inv)}>
                    <Eye size={14} className="mr-1.5" />
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {activeInvoice && (
        <InvoiceDetailModal invoice={activeInvoice} onClose={() => setActiveInvoice(null)} />
      )}
    </div>
  )
}
